import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './LunchRouletteShared.module.css';
import { 
  lunchRouletteApi,
  LunchRouletteUserMatch
} from '../../services/lunchRouletteApi';
import { useUserContext } from '../../contexts/UserContext';

const LunchRouletteMyMatches: React.FC = () => {
  const navigate = useNavigate();
  const { rouletteId } = useParams<{ rouletteId: string }>();
  const { user } = useUserContext();
  
  // Data states
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<LunchRouletteUserMatch | null>(null);

  // Helper function to extract error message from error objects
  const getErrorMessage = (error: any): string => {
    if (error instanceof Error) {
      return error.message;
    }
    
    if (typeof error === 'object' && error !== null) {
      // Try to extract message from axios or custom API error
      if ('message' in error) return error.message;
      if ('response' in error && error.response?.data?.detail) return error.response.data.detail;
      if ('response' in error && error.response?.data?.message) return error.response.data.message;
    }
    
    return 'Unknown error occurred';
  };

  // Fetch user matches data
  const fetchUserMatches = async () => {
    if (!rouletteId) return;

    try {
      setLoading(true);
  const matchData = await lunchRouletteApi.getUserMatches(rouletteId);
      setData(matchData);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to load matches: ${errorMessage}`);
      console.error('Error fetching user matches:', error);
      
      // If user is not registered or not authorized, redirect to overview
      if ((error as any)?.message?.includes('403') || (error as any)?.message?.includes('not registered')) {
        navigate('/lunch-roulette');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time helper
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Check if lunch date is in the past
  const isLunchDatePast = (lunchDate: string): boolean => {
    const today = new Date();
    const lunch = new Date(lunchDate);
    today.setHours(0, 0, 0, 0);
    lunch.setHours(0, 0, 0, 0);
    return lunch < today;
  };

  // Generate Teams chat URL for group
  const getTeamsGroupChatUrl = (members: any[]): string => {
    const emails = members.map(member => member.mail).join(',');
    const topicName = `Lunch Roulette ${formatDate(data!.roulette.lunch_date).replace(/\s/g, '%20')}`;
    return `https://teams.microsoft.com/l/chat/0/0?users=${emails}&topicName=${topicName}`;
  };

  // Generate Teams individual chat URL
  const getTeamsIndividualChatUrl = (email: string): string => {
    return `https://teams.microsoft.com/l/chat/0/0?users=${email}`;
  };

  // Get user avatar initials
  const getUserInitials = (firstname: string, lastname: string): string => {
    return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
  };

  useEffect(() => {
    fetchUserMatches();
  }, [rouletteId]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading your matches...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.errorContainer}>
        <div className="alert alert-danger">
          <h5>Error Loading Matches</h5>
          <p>Unable to load your matches for this Lunch Roulette.</p>
          <button 
            className="btn btn-outline-primary"
            onClick={() => navigate('/lunch-roulette')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  const { roulette, is_registered, my_match_group, user_id } = data;

  return (
    <div className="container mt-4">
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb" className={styles.breadcrumb}>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <button 
              className="btn btn-link p-0"
              onClick={() => navigate('/lunch-roulette')}
            >
              Lunch Roulettes
            </button>
          </li>
          <li className="breadcrumb-item">
            {formatDate(roulette.lunch_date)}
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            My Matches
          </li>
        </ol>
      </nav>

      {/* Roulette Header */}
      <div className={styles.eventHeader}>
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Lunch Roulette Details
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h5>
                      <i className="bi bi-calendar-event me-2"></i>
                      Lunch Date
                    </h5>
                    <p className="lead">
                      {formatDate(roulette.lunch_date)} at {formatTime(roulette.lunch_date)}
                    </p>
                    
                    <h5 className="mt-3">
                      <i className="bi bi-people me-2"></i>
                      Group Size
                    </h5>
                    <p className="lead">{roulette.group_size} people</p>
                  </div>
                  <div className="col-md-6">
                    <h5>
                      <i className="bi bi-globe me-2"></i>
                      Event Type
                    </h5>
                    <p className="lead">
                      {roulette.is_hybrid ? (
                        <>
                          <i className="bi bi-camera-video-fill me-2"></i>
                          Hybrid (via Microsoft Teams)
                        </>
                      ) : (
                        <>
                          <i className="bi bi-geo-alt-fill me-2"></i>
                          In-Person Event
                        </>
                      )}
                    </p>

                    <h5 className="mt-3">
                      <i className="bi bi-hourglass-split me-2"></i>
                      Registration Period
                    </h5>
                    <p className="lead">
                      {formatDate(roulette.registration_start)} to {formatDate(roulette.registration_end)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status/Information Alert */}
        {!is_registered && (
          <div className="alert alert-warning mb-4">
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2 fs-3"></i>
              <div>
                <h5 className="alert-heading">Not Registered</h5>
                <p className="mb-0">
                  You are not registered for this Lunch Roulette. To participate in this event, you need to register.
                </p>
                <button 
                  className="btn btn-warning mt-2"
                  onClick={() => navigate(`/lunch-roulette?open_register=${roulette.Roulette_ID}`)}
                >
                  <i className="bi bi-pencil-square me-2"></i>
                  Register Now
                </button>
              </div>
            </div>
          </div>
        )}

        {is_registered && !my_match_group && (
          <>
            {isLunchDatePast(roulette.lunch_date) ? (
              <div className="alert alert-secondary mb-4">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle-fill me-2 fs-3"></i>
                  <div>
                    <h5 className="alert-heading">No Matches Available</h5>
                    <p className="mb-0">
                      No matches were found for you in this past Lunch Roulette.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <i className="bi bi-hourglass-split me-2 fs-3"></i>
                  <div>
                    <h5 className="alert-heading">Matches Being Created</h5>
                    <p className="mb-0">
                      You are registered for this Lunch Roulette, but matches have not been created yet. Please check back later.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* My Matches Area */}
        {my_match_group && (
          <>
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                  <i className="bi bi-people-fill me-2"></i>
                  My Lunch Roulette Group
                </h5>
              </div>
              <div className="card-body">
                <div className="alert alert-success mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill me-2 fs-3"></i>
                    <div>
                      <h5 className="alert-heading">Your Matches Have Been Created!</h5>
                      <p className="mb-0">
                        Here are your lunch partners for this event. Please contact your group to arrange a meeting time.
                      </p>
                    </div>
                  </div>
                </div>

                {my_match_group.members.length > 2 && (
                  <div className="mb-4">
                    <div className="d-grid">
                      <a 
                        href={getTeamsGroupChatUrl(my_match_group.members)}
                        className="btn btn-primary mb-4" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-people-fill me-2"></i>
                        Start Group Chat with Everyone
                      </a>
                    </div>
                  </div>
                )}

                {/* Team members cards */}
                <div className="row">
                  {my_match_group.members
                    .filter(member => member.User_ID !== user_id)
                    .map((member) => (
                      <div key={member.User_ID} className="col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm border-0">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-3">
                              {/* Profile Picture */}
                              {member.picture ? (
                                <img 
                                  className="rounded-circle me-3" 
                                  src={`data:image/png;base64,${member.picture}`}
                                  alt={`${member.firstname} ${member.lastname}`}
                                  style={{ width: '5rem', height: '5rem', objectFit: 'cover' }}
                                />
                              ) : (
                                <div 
                                  className={`${styles.userAvatar} me-3`}
                                  style={{ width: '5rem', height: '5rem', fontSize: '1.5rem' }}
                                >
                                  {getUserInitials(member.firstname, member.lastname)}
                                </div>
                              )}
                              
                              <div>
                                <h5 className="card-title mb-1">
                                  {member.firstname} {member.lastname}
                                </h5>
                                {member.jobtitle && (
                                  <p className="card-text text-muted mb-1">
                                    <i className="bi bi-briefcase me-2"></i>
                                    {member.jobtitle}
                                  </p>
                                )}
                                {member.mail && (
                                  <span className="text-decoration-none">
                                    <i className="bi bi-envelope me-1"></i>
                                    {member.mail}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="mt-3">
                              <div className="mb-2">
                                {member.department && (
                                  <div className="d-flex align-items-center mb-2">
                                    <span className="badge bg-light text-dark me-2 p-2">
                                      <i className="bi bi-building me-1"></i>
                                      Department
                                    </span>
                                    <span>{member.department}</span>
                                  </div>
                                )}
                                
                                {member.employee_group && (
                                  <div className="d-flex align-items-center mb-2">
                                    <span className="badge bg-light text-dark me-2 p-2">
                                      <i className="bi bi-people me-1"></i>
                                      Employee Group
                                    </span>
                                    <span>{member.employee_group}</span>
                                  </div>
                                )}
                                
                                {member.city_name && (
                                  <div className="d-flex align-items-center mb-2">
                                    <span className="badge bg-light text-dark me-2 p-2">
                                      <i className="bi bi-geo-alt me-1"></i>
                                      Location
                                    </span>
                                    <span>{member.city_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="card-footer bg-white border-0 pt-0">
                            <div className="d-grid">
                              <a 
                                href={getTeamsIndividualChatUrl(member.mail)}
                                className="btn btn-outline-primary" 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                <i className="bi bi-microsoft-teams me-2"></i>
                                Contact via Teams
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            
            {/* Your information in this match */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <i className="bi bi-person-badge me-2"></i>
                  Your Information in this Match
                </h5>
              </div>
              <div className="card-body">
                <div className="alert alert-light mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle me-2 fs-3"></i>
                    <div>
                      <h5 className="alert-heading">This is what your match partners see</h5>
                      <p className="mb-0">
                        This is how your data appears to other participants in this Lunch Roulette.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="row justify-content-center">
                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-body p-4">
                        {my_match_group.members
                          .filter(member => member.User_ID === user_id)
                          .map((member) => (
                            <div key={member.User_ID}>
                              <div className="d-flex align-items-center mb-3">
                                {/* Profile Picture */}
                                {member.picture ? (
                                  <img 
                                    className="rounded-circle me-3" 
                                    src={`data:image/png;base64,${member.picture}`}
                                    alt={`${member.firstname} ${member.lastname}`}
                                    style={{ width: '5rem', height: '5rem', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div 
                                    className={`${styles.userAvatar} me-3`}
                                    style={{ width: '5rem', height: '5rem', fontSize: '1.5rem' }}
                                  >
                                    {getUserInitials(member.firstname, member.lastname)}
                                  </div>
                                )}
                                
                                <div>
                                  <h5 className="card-title mb-1">
                                    {member.firstname} {member.lastname}
                                  </h5>
                                  {member.jobtitle && (
                                    <p className="card-text text-muted mb-1">
                                      <i className="bi bi-briefcase me-2"></i>
                                      {member.jobtitle}
                                    </p>
                                  )}
                                  {member.mail && (
                                    <span className="text-decoration-none">
                                      <i className="bi bi-envelope me-1"></i>
                                      {member.mail}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="mt-3">
                                <div className="mb-2">
                                  {member.department && (
                                    <div className="d-flex align-items-center mb-2">
                                      <span className="badge bg-light text-dark me-2 p-2">
                                        <i className="bi bi-building me-1"></i>
                                        Department
                                      </span>
                                      <span>{member.department}</span>
                                    </div>
                                  )}
                                  
                                  {member.employee_group && (
                                    <div className="d-flex align-items-center mb-2">
                                      <span className="badge bg-light text-dark me-2 p-2">
                                        <i className="bi bi-people me-1"></i>
                                        Employee Group
                                      </span>
                                      <span>{member.employee_group}</span>
                                    </div>
                                  )}
                                  
                                  {member.city_name && (
                                    <div className="d-flex align-items-center mb-2">
                                      <span className="badge bg-light text-dark me-2 p-2">
                                        <i className="bi bi-geo-alt me-1"></i>
                                        Location
                                      </span>
                                      <span>{member.city_name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Return button */}
        <div className="mt-4 text-center">
          <button 
            className="btn btn-outline-primary"
            onClick={() => navigate('/lunch-roulette')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Overview
          </button>
        </div>
      </div>
    </div>
  );
};

export default LunchRouletteMyMatches;
